import { useState, useEffect, useRef } from "react";
import { db, auth } from "./firebase";
import {
  collection, getDoc, doc, writeBatch, onSnapshot
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import "./PressFeeTracker.css";
import { writeAuditLog } from "./utils/auditLog";
import { TEAM_MEMBERS } from "./utils/teamMembers";
import useTeamDirectory, { getTeamRealName } from "./hooks/useTeamDirectory";
import {
  APPLY_WHT,
  getShopPctForTeam,
  calculateFeeAmounts,
  shouldApplyWhtForTeam,
  canonicalTeamName,
  isSalariedTeam,
  getBangkokDateParts,
} from "./utils/feeRules";

const FIXED_TEAM_LIST = TEAM_MEMBERS;

/**
 * PressFeeTracker (Multi-rows)
 * - กรอกหลายงาน/หลายโซน/หลายจำนวน พร้อมกันในฟอร์มเดียว
 * - เห็นยอดต่อแถว + สรุปรวมทุกแถว
 * - ค่าคอมคิดตามชื่อทีมเท่านั้น ไม่แยกตามประเภทงาน
 * - ไม่แสดง "ส่วนของร้าน" แต่แสดง "จำนวนเงินที่ถูกหัก"
 * - ไม่มีช่องหมายเหตุกลาง (ช่องแรก)
 */

export default function PressFeeTracker({ previewMode = false }) {
  // ====== กติกาค่ากด ======
  // ใช้กติกากลางจาก utils/feeRules.js เพื่อให้ทุกหน้าคำนวณตรงกัน

  // ====== ข้อมูลผู้ใช้และอีเวนต์ ======
  const [events, setEvents] = useState([]);
  const [user, setUser] = useState(null);
  const [, setRole] = useState("team");
  const [isTeamLinked, setIsTeamLinked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ruleClock, setRuleClock] = useState(() => new Date());
  const submitLockRef = useRef(false);

  // ทีม (fixed list)
  const [teamName, setTeamName] = useState("");
  const { realNameByTeam } = useTeamDirectory();
  const teamRealName = getTeamRealName(teamName, realNameByTeam);

  // ====== โครงแถวหลายรายการ ======
  // 1 แถว = 1 รายการ (งาน/โซน/จำนวน)
  const blankRow = () => ({
    eventId: "",
    eventObj: null,
    priceLabel: "",
    priceObj: null, // { label/price, fee }
    quantity: 1,
  });

  const [rows, setRows] = useState([blankRow()]);

  // ====== โหลด user/role ======
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setUser(u || null);
      if (!u) return;

      const snap = await getDoc(doc(db, "users", u.uid));
      const profile = snap.exists() ? snap.data() : {};
      const nextRole = profile.role || "team";
      setRole(nextRole);

      // ทีมทั่วไปต้องใช้ชื่อทีมที่แอดมินผูกกับบัญชีเท่านั้น
      // ไม่เดาจาก displayName และไม่ให้ผู้ใช้เลือกตัวตนเองครั้งแรก เพื่อกันกรอกแทนคนอื่น
      const linkedName = canonicalTeamName(profile.teamName);
      const isValidLinkedTeam = FIXED_TEAM_LIST.includes(linkedName);

      if (previewMode) {
        // โหมด preview ของแอดมินเลือกทีมเพื่อดูหน้าตา/การคำนวณได้ แต่บันทึกจริงไม่ได้
        setTeamName(isValidLinkedTeam ? linkedName : "");
        setIsTeamLinked(isValidLinkedTeam);
      } else if ((nextRole === "team" || nextRole === "admin") && isValidLinkedTeam) {
        // ทุกบัญชีที่กรอกค่ากดต้องใช้ชื่อทีมที่แอดมินผูกกับบัญชีของตัวเองเท่านั้น
        // ไม่มีโหมดเลือกชื่อเพื่อกรอกแทนคนอื่น แม้เป็น admin
        setTeamName(linkedName);
        setIsTeamLinked(true);
      } else {
        setTeamName("");
        setIsTeamLinked(false);
      }
    });
    return () => unsub();
  }, [previewMode]);

  // อัปเดตกติกาตามเวลาไทยอัตโนมัติ แม้เปิดหน้าค้างข้ามวันที่/ข้ามเดือน
  useEffect(() => {
    const timer = setInterval(() => setRuleClock(new Date()), 60 * 1000);
    return () => clearInterval(timer);
  }, []);

  // ====== โหลดรายการงานแบบ realtime ======
  // แอดมินแก้โซน/ราคา/ค่ากดของงานที่กำลังใช้อยู่แล้ว ทีมจะได้รับข้อมูลใหม่ทันที
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "events"), (snapshot) => {
      const fetched = snapshot.docs
        .map((dx) => ({ id: dx.id, ...dx.data() }))
        .filter((ev) => !ev.archived);
      setEvents(fetched);
    });

    return unsubscribe;
  }, []);

  // ถ้าแอดมินแก้งานขณะที่ทีมเปิดฟอร์มอยู่ ให้ sync event object ในแถวที่เลือกไว้
  // ถ้าโซนที่เลือกถูกแก้ชื่อ/ลบ จะล้างโซนนั้นเพื่อบังคับให้เลือกค่าล่าสุดใหม่
  useEffect(() => {
    setRows((previous) =>
      previous.map((row) => {
        if (!row.eventId) return row;

        const latestEvent = events.find((event) => event.id === row.eventId) || null;
        if (!latestEvent) {
          return { ...row, eventObj: null, priceLabel: "", priceObj: null };
        }

        const latestPrices = latestEvent.prices || latestEvent.tickets || [];
        const latestPrice = latestPrices.find(
          (item) => `${item.label || item.price}` === row.priceLabel
        ) || null;

        return {
          ...row,
          eventObj: latestEvent,
          priceLabel: latestPrice ? `${latestPrice.label || latestPrice.price}` : "",
          priceObj: latestPrice,
        };
      })
    );
  }, [events]);

  // ====== ช่วยคำนวณ ======
  const getShopPct = (tName, at = new Date()) => getShopPctForTeam(tName, at);

  const feePer = (row) => Number(row?.priceObj?.fee || 0);
  const qtyOf = (row) => Number(row?.quantity || 0);

  const perRowCalc = (row) => {
    const result = calculateFeeAmounts({
      teamName,
      feePerTicket: feePer(row),
      quantity: qtyOf(row),
      effectiveAt: ruleClock,
    });

    return {
      fee: result.feePer,
      qty: result.qty,
      baseFee: result.baseFee,
      totalCompCut: result.deducted,
      teamBeforeTax: result.teamBeforeTax,
      wht: result.tax,
      net: result.net,
      applyWht: result.applyWht,
    };
  };

  const allCalc = rows.map(perRowCalc);

  const totals = allCalc.reduce((acc, c) => {
      acc.totalQty += c.qty;
      acc.totalBase += c.baseFee;
      acc.totalDeduct += c.totalCompCut;
      acc.totalWHT += c.wht;
      acc.totalNet += c.net;
      return acc;
    }, { totalQty: 0, totalBase: 0, totalDeduct: 0, totalWHT: 0, totalNet: 0 });

  // ====== อัปเดตค่าแถว ======
  const updateRow = (index, patch) => {
    setRows((prev) => {
      const cp = [...prev];
      cp[index] = { ...cp[index], ...patch };
      return cp;
    });
  };

  const onChangeEvent = (index, eventId) => {
    const ev = events.find((e) => e.id === eventId) || null;
    updateRow(index, {
      eventId,
      eventObj: ev,
      priceLabel: "",
      priceObj: null,
    });
  };

  const currentPrices = (row) =>
    row.eventObj ? (row.eventObj.prices || row.eventObj.tickets || []) : [];

  const onChangePrice = (index, priceLabel) => {
    const row = rows[index];
    const p = currentPrices(row).find(
      (x) => `${x.label || x.price}` === priceLabel
    ) || null;
    updateRow(index, { priceLabel, priceObj: p });
  };

  const addRow = () => setRows((prev) => [...prev, blankRow()]);
  const removeRow = (i) => setRows((prev) => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  // ====== บันทึกทั้งหมด (หลายเอกสารในครั้งเดียว) ======
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (previewMode) {
      alert("โหมดดูในมุมทีมเป็นตัวอย่างเท่านั้น ข้อมูลจะไม่ถูกบันทึกจริง");
      return;
    }

    // กัน double-click แบบ synchronous ก่อน React จะ render disabled state
    if (submitLockRef.current || isSubmitting) return;
    if (!user) return;
    if (!teamName || !isTeamLinked) {
      alert("บัญชีนี้ยังไม่ได้ผูกชื่อทีมสำหรับการกรอกค่ากด กรุณาติดต่อแอดมิน");
      return;
    }

    const validRows = rows.filter((r) =>
      r.eventId && r.priceObj && qtyOf(r) > 0
    );
    if (validRows.length === 0) {
      alert("โปรดกรอกอย่างน้อย 1 รายการให้ครบถ้วน");
      return;
    }

    submitLockRef.current = true;
    setIsSubmitting(true);

    try {
      // submissionId เดียวต่อการกดบันทึกหนึ่งครั้ง
      // record ID จะผูกกับ submissionId ทำให้ retry ชุดเดิมไม่สร้างรายการซ้ำ
      const submissionId =
        typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : `${user.uid}-${Date.now()}-${Math.random().toString(36).slice(2)}`;

      const batch = writeBatch(db);
      const colRef = collection(db, `user-press-fee/${user.uid}/records`);
      const now = new Date();

      validRows.forEach((r, index) => {
        const c = perRowCalc(r);
        const ref = doc(colRef, `${submissionId}_${index + 1}`);

        batch.set(ref, {
          submissionId,
          submissionRow: index + 1,
          submittedByUid: user.uid,
          submittedByEmail: user.email || "",
          eventId: r.eventId || r.eventObj?.id || "",
          eventName: r.eventObj?.name || r.eventObj?.eventName || "",
          zone: r.priceObj?.label || r.priceObj?.price || "",
          feePerTicket: c.fee,
          quantity: c.qty,
          totalFee: c.baseFee,
          commissionRate: getShopPct(teamName, now),
          totalDeducted: c.totalCompCut,
          teamBeforeTax: c.teamBeforeTax,
          applyWht: !!c.applyWht,
          withholding3pct: c.wht,
          netIncome: c.net,
          calculationSnapshot: true,
          teamName: canonicalTeamName(teamName),
          teamRealName: teamRealName || "",
          paymentRequired: canonicalTeamName(teamName) !== "ยย",
          isPaid: canonicalTeamName(teamName) === "ยย",
          paidAt: canonicalTeamName(teamName) === "ยย" ? now : null,
          createdAt: now,
        });
      });

      await batch.commit();
      await writeAuditLog({
        user, action: "press-fee.create", entityType: "press-fee", entityId: submissionId,
        title: `บันทึกค่ากด ${validRows.length} รายการ`,
        after: { teamName: canonicalTeamName(teamName), recordCount: validRows.length, createdAt: now },
      }).catch((error) => console.error("audit log failed", error));

      alert(`🐰✅ บันทึก ${validRows.length} รายการเรียบร้อยแล้ว!`);
      setRows([blankRow()]);
    } catch (error) {
      console.error("บันทึกค่ากดไม่สำเร็จ", error);
      alert("บันทึกไม่สำเร็จ กรุณาลองใหม่อีกครั้ง");
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  // ====== UI ======
  const pctText = () => {
    if (!teamName) return "";
    const shopPct = getShopPct(teamName, ruleClock);
    const teamPct = Math.max(0, 1 - shopPct);
    return `หักค่าคอม ${Math.round(shopPct * 100)}% · ทีมได้รับก่อนภาษี ${Math.round(teamPct * 100)}%`;
  };

  const fmt = (n) => Number(n || 0).toLocaleString("th-TH", {
    maximumFractionDigits: 2,
  });

  const isRowComplete = (row) => Boolean(row.eventId && row.priceObj && qtyOf(row) > 0);
  const completedRows = rows.filter(isRowComplete).length;
  const hasIncompleteRows = rows.some((row) => !isRowComplete(row));
  const bangkokToday = getBangkokDateParts(ruleClock);
  const salaryEntryLocked = false;
  const salaryAfterCutoff = Boolean(teamName) && isSalariedTeam(teamName) && bangkokToday.day > 25;
  const canSubmit = Boolean(teamName) && rows.length > 0 && !hasIncompleteRows && !isSubmitting && !salaryEntryLocked;

  const changeQuantity = (index, delta) => {
    const current = Math.max(1, qtyOf(rows[index]) || 1);
    updateRow(index, { quantity: Math.max(1, current + delta) });
  };

  return (
    <div className="fee-page">
      <div className="fee-shell">
        <header className="fee-page-header">
          <div>
            <div className="fee-eyebrow">PRESS FEE</div>
            <h1>กรอกค่ากด</h1>
            <p>เลือกงาน โซน และจำนวนบัตร ระบบจะคำนวณยอดให้ทันที</p>
          </div>
          <div className="fee-header-status">
            <span className="status-dot" />
            {previewMode ? "โหมดดูตัวอย่าง" : salaryEntryLocked ? "ปิดรอบกรอกค่ากด" : "พร้อมบันทึกข้อมูล"}
          </div>
        </header>

        <form className="fee-form" onSubmit={handleSubmit}>
          <section className="operator-card">
            <div className="operator-icon" aria-hidden="true">👤</div>
            <div className="operator-copy">
              <span className="section-kicker">ผู้ดำเนินการ</span>

              {previewMode ? (
                <div className="operator-select-wrap">
                  <select
                    required
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    className="modern-select operator-select"
                    disabled={isSubmitting}
                    aria-label="เลือกทีมสำหรับดูตัวอย่าง"
                  >
                    <option value="">เลือกทีมสำหรับดูตัวอย่าง</option>
                    {FIXED_TEAM_LIST.map((n) => <option key={n} value={n}>{getTeamRealName(n, realNameByTeam) ? `${n} · ${getTeamRealName(n, realNameByTeam)}` : n}</option>)}
                  </select>
                  <span className="operator-badge admin">PREVIEW</span>
                </div>
              ) : isTeamLinked ? (
                <div className="operator-linked-row">
                  <div className="operator-name-stack">
                    <strong>{teamName}</strong>
                    {teamRealName && <small>{teamRealName}</small>}
                  </div>
                  <span className="operator-badge">กรอกได้เฉพาะของตัวเอง</span>
                </div>
              ) : (
                <div className="operator-first-link">
                  <strong>บัญชียังไม่ได้ผูกชื่อทีม</strong>
                  <small>กรุณาติดต่อแอดมินให้กำหนดชื่อทีมในหน้า “จัดการผู้ใช้” ก่อนเริ่มกรอกค่ากด</small>
                </div>
              )}
            </div>

            {teamName && (
              <div className="rule-chips">
                <span className="rule-chip commission">ค่าคอม {Math.round(getShopPct(teamName, ruleClock) * 100)}%</span>
                <span className={`rule-chip ${shouldApplyWhtForTeam(teamName) ? "tax" : "no-tax"}`}>
                  {shouldApplyWhtForTeam(teamName) ? "หัก ณ ที่จ่าย 3%" : "ไม่หัก ณ ที่จ่าย"}
                </span>
              </div>
            )}
          </section>

          {salaryAfterCutoff && (
            <section className="salary-lock-banner">
              <div className="salary-lock-icon" aria-hidden="true">📅</div>
              <div>
                <strong>บันทึกได้ตามปกติ · เข้ารอบโอนเดือนถัดไป</strong>
                <p>{canonicalTeamName(teamName)} ยังกรอกค่ากดได้ตามปกติ แต่รายการหลังวันที่ 25 จะไปรวมยอดโอนวันที่ 26 ของเดือนถัดไป</p>
              </div>
              <span className="salary-lock-date">วันนี้ {bangkokToday.day} / {bangkokToday.month}</span>
            </section>
          )}

          <div className="fee-layout">
            <main className="fee-entry-column">
              <div className="entry-heading">
                <div>
                  <span className="section-kicker">รายการค่ากด</span>
                  <h2>{rows.length} รายการ</h2>
                </div>
                <div className="entry-progress" aria-label={`กรอกครบ ${completedRows} จาก ${rows.length} รายการ`}>
                  <span className={completedRows === rows.length ? "complete" : ""}>
                    {completedRows}/{rows.length} กรอกครบ
                  </span>
                </div>
              </div>

              <div className="fee-items">
                {rows.map((row, i) => {
                  const prices = currentPrices(row);
                  const calc = perRowCalc(row);
                  const complete = isRowComplete(row);

                  return (
                    <article className={`fee-item-card ${complete ? "is-complete" : ""}`} key={i}>
                      <div className="fee-item-head">
                        <div className="item-number-wrap">
                          <span className="item-number">{String(i + 1).padStart(2, "0")}</span>
                          <div>
                            <strong>รายการที่ {i + 1}</strong>
                            <small>{complete ? "พร้อมบันทึก" : "กรอกข้อมูลให้ครบ"}</small>
                          </div>
                        </div>
                        <div className="item-head-actions">
                          <span className={`item-state ${complete ? "ready" : "pending"}`}>
                            {complete ? "พร้อม" : "ยังไม่ครบ"}
                          </span>
                          {rows.length > 1 && (
                            <button
                              type="button"
                              className="icon-delete-btn"
                              onClick={() => removeRow(i)}
                              disabled={isSubmitting || salaryEntryLocked}
                              aria-label={`ลบรายการที่ ${i + 1}`}
                              title="ลบรายการ"
                            >
                              ×
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="field-grid">
                        <label className="field-group field-event">
                          <span>ชื่องาน</span>
                          <select
                            required
                            value={row.eventId}
                            onChange={(e) => onChangeEvent(i, e.target.value)}
                            className="modern-select"
                            disabled={isSubmitting || salaryEntryLocked}
                          >
                            <option value="">เลือกงาน</option>
                            {events.map((ev) => (
                              <option key={ev.id} value={ev.id}>
                                {ev.name || ev.eventName}
                              </option>
                            ))}
                          </select>
                        </label>

                        <label className="field-group field-zone">
                          <span>โซน / ราคาบัตร</span>
                          <select
                            required
                            value={row.priceLabel}
                            onChange={(e) => onChangePrice(i, e.target.value)}
                            className="modern-select"
                            disabled={!row.eventObj || isSubmitting || salaryEntryLocked}
                          >
                            <option value="">{row.eventObj ? "เลือกโซน / ราคา" : "เลือกงานก่อน"}</option>
                            {prices.map((p, idx) => (
                              <option key={idx} value={p.label || p.price}>
                                {(p.label || p.price)} · ค่ากด {fmt(p.fee)} บาท
                              </option>
                            ))}
                          </select>
                        </label>

                        <div className="field-group field-quantity">
                          <span>จำนวนบัตร</span>
                          <div className="quantity-control">
                            <button
                              type="button"
                              onClick={() => changeQuantity(i, -1)}
                              disabled={isSubmitting || salaryEntryLocked || qtyOf(row) <= 1}
                              aria-label="ลดจำนวนบัตร"
                            >−</button>
                            <input
                              type="number"
                              min={1}
                              value={row.quantity}
                              onChange={(e) => updateRow(i, { quantity: Math.max(1, Number(e.target.value) || 1) })}
                              disabled={isSubmitting || salaryEntryLocked}
                              aria-label="จำนวนบัตร"
                            />
                            <button
                              type="button"
                              onClick={() => changeQuantity(i, 1)}
                              disabled={isSubmitting || salaryEntryLocked}
                              aria-label="เพิ่มจำนวนบัตร"
                            >+</button>
                          </div>
                        </div>
                      </div>

                      <div className="item-calculation">
                        <div className="calc-cell">
                          <span>ค่ากดรวม</span>
                          <strong>{fmt(calc.baseFee)} <small>บาท</small></strong>
                        </div>
                        <div className="calc-cell muted">
                          <span>หักค่าคอม</span>
                          <strong>−{fmt(calc.totalCompCut)} <small>บาท</small></strong>
                        </div>
                        <div className="calc-cell muted">
                          <span>{calc.applyWht ? "หัก ณ ที่จ่าย" : "ภาษี ณ ที่จ่าย"}</span>
                          <strong>{calc.applyWht ? `−${fmt(calc.wht)}` : "0"} <small>บาท</small></strong>
                        </div>
                        <div className="calc-cell net">
                          <span>ได้รับจริง</span>
                          <strong>{fmt(calc.net)} <small>บาท</small></strong>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>

              <button
                type="button"
                className="add-item-button"
                onClick={addRow}
                disabled={isSubmitting || salaryEntryLocked}
              >
                <span className="add-icon">+</span>
                <span>
                  <strong>เพิ่มรายการ</strong>
                  <small>เพิ่มงาน / โซน / จำนวนอีกหนึ่งรายการ</small>
                </span>
              </button>
            </main>

            <aside className="fee-summary-column">
              <div className="summary-card">
                <div className="summary-head">
                  <div>
                    <span className="section-kicker">สรุปยอด</span>
                    <h2>ยอดค่ากดทั้งหมด</h2>
                  </div>
                  <span className="summary-count">{totals.totalQty} ใบ</span>
                </div>

                {teamName ? (
                  <div className="summary-rule">
                    <div className="summary-team-name">
                      <strong>{teamName}</strong>
                      {teamRealName && <small>{teamRealName}</small>}
                    </div>
                    <span>{pctText()}</span>
                  </div>
                ) : (
                  <div className="summary-rule empty">เลือกชื่อทีมเพื่อดูอัตราค่าคอม</div>
                )}

                <div className="summary-lines">
                  <div>
                    <span>ค่ากดรวม</span>
                    <strong>{fmt(totals.totalBase)} บาท</strong>
                  </div>
                  <div>
                    <span>หักค่าคอม</span>
                    <strong className="deduction">−{fmt(totals.totalDeduct)} บาท</strong>
                  </div>
                  {APPLY_WHT && (
                    <div>
                      <span>หัก ณ ที่จ่าย</span>
                      <strong className="deduction">−{fmt(totals.totalWHT)} บาท</strong>
                    </div>
                  )}
                </div>

                <div className="summary-net">
                  <span>ได้รับจริงสุทธิ</span>
                  <strong>{fmt(totals.totalNet)}</strong>
                  <small>บาท</small>
                </div>

                {hasIncompleteRows && (
                  <div className="form-warning">
                    <span>!</span>
                    <p>ยังมีรายการที่กรอกไม่ครบ กรุณาเลือกงานและโซนให้ครบก่อนบันทึก</p>
                  </div>
                )}

                <button
                  type="submit"
                  className="save-all-button"
                  disabled={!canSubmit}
                >
                  {isSubmitting ? (
                    <><span className="button-spinner" /> กำลังบันทึก...</>
                  ) : previewMode ? (
                    <>ดูตัวอย่างปุ่มบันทึก <span>→</span></>
                  ) : (
                    <>บันทึก {rows.length} รายการ <span>→</span></>
                  )}
                </button>

                <p className="save-helper">
                  {previewMode
                    ? "โหมดดูในมุมทีมจะไม่บันทึกหรือแก้ไขข้อมูลจริง"
                    : salaryAfterCutoff
                      ? "กรอกได้ตามปกติ · ยอดพนักงานจะเข้ารอบวันที่ 26 ของเดือนถัดไป"
                      : "ระบบล็อกปุ่มระหว่างบันทึกเพื่อป้องกันการส่งข้อมูลซ้ำ"}
                </p>
              </div>
            </aside>
          </div>
        </form>
      </div>
    </div>
  );
}
